import { DialogueNotificationsConversation } from '../screens/NotificationsPersonnelles.js';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type JSX } from 'react';
import { useUser } from '@taki/auth';
import type { TakiConfig } from '@taki/runtime-config';
import { messageDuSuiviEnMots } from '@taki/data-model';
import {
  Button, IconPadlockSquare1, IconArrowRight, IconWindowAdd, IconWindowExpand, IconWindowMinimize, IconWindowShrink,
  Modal, cn, useConversationsCentrePort, useDeclarationsPort, useIaSettingsPort, useLiaConversationsPort,
  useNotificationsPort, usePersonnesPort, useSourceDeMentions,
  type ConversationVue, type DestinationDeFiche, type LiaSujetPartageView, type LiaSujetView, type MessageVue,
  initialesDe as initialesDuFil,
} from '@taki/ui';
import { NotificationsBoite } from '../notifications/NotificationsBoite.js';
import { nonLusDuCentre } from '../notifications/source.js';
import { ancreStricte, chattersDeFicheAnnonces, EVENEMENT_CHATTER_FICHE, EVENEMENT_OUVRIR_CENTRE, gesteDuLanceur, ouvrirCentre, messageDuChemin, signalerChatterEcrit, type OuvrirCentre } from './ouvrirCentre.js';
import type { ShellAppEntry } from '../types.js';
import { LiaHead, LiaLauncher, type LiaUnread } from '../surfaces/LiaLauncher.js';
import type { AskContext } from '../surfaces/askTakiClient.js';
import { ConversationLia, type SujetEnCours } from './ConversationLia.js';
import { useLiaPanelSize } from '../surfaces/liaPanelSize.js';
import { IconeEpingle } from './IconeEpingle.js';
import { ConversationSocle } from './ConversationSocle.js';
import { catalogueDeLaFiche, declarationDeLApp } from './suiviEnMots.js';
import { ChoixDePersonnes, FenetreCanal } from './ChoixDePersonnes.js';
import { EmplacementAVenir } from './EmplacementAVenir.js';
import { IconeDeSource, RailDuCentre, ListeDuRail } from './RailDuCentre.js';
import { ficheOuverte as ficheDuContexte, libelleDuContexte, type ContexteOuvert, type FicheSuivie } from './contexte.js';
import {
  bornerLargeurListe, CLE_ETAT_CENTRE, cleEtatCentre, echapFermeLaFenetre, ecrireEtatCentre, estRaccourciRail, FENETRE_MARGE, geometrieFenetre, lireEtatCentre,
  listeSeSuperpose, type EtatCentre,
} from './etatCentre.js';
import { sourceDuCentre, type ConversationResume, type SourceId } from './sources.js';
import { ancreDeFiche, memeAncre, personneDeDirect, raisonLectureSeuleIci, refDEpingle, resumesDeConversations, titreDeDirect, titreDeLAncre, useSourcesConversations } from './sourcesConversations.js';
import {
  initialesDe, resumeDeSujet, sujetCourant, titreDepuisQuestion, TITRE_SUJET_DEFAUT, tourDepuisStockage, tourPourStockage, trierSujets,
} from './sujetsLia.js';

/**
 * LE CENTRE DE COMMUNICATION (chantier `centre-communication`, DA validee par
 * le fondateur le 05/10/2026 : maquettes hub-a, hub-b, hub-d, hub-e1, hub-e2).
 *
 * Il remplace la fenetre flottante de Lia. Deux formes, un seul etat :
 *
 * - COMPACT (hub-d, hub-e1, hub-e2) : une FENETRE flottante posee au-dessus
 *   de l'app, ancree en bas a droite avec ses marges (l'app reste visible et
 *   utilisable derriere), en taille compacte ou plus haute. A gauche un rail
 *   d'icones (Notifications, Ici, Lia, Canaux, Messages directs) ; la fleche
 *   en pied de rail DEPLIE la liste des conversations, qui remplace le rail
 *   et ELARGIT la fenetre vers la gauche (la conversation ne bouge pas).
 *   Fenetre du navigateur sous 1100 px : la liste se superpose (hub-e3).
 * - AGRANDI (hub-a, hub-b) : le centre entier a la place de la zone de
 *   travail, rail de portes du gabarit a gauche (`ScreenLayout`, groupes avec
 *   « + »), recherche en 2b, rangee 3 avec les gestes de la conversation (G7),
 *   fil plat en zone 5, colonne « Fiche suivie » a droite.
 *
 * Les CINQ SOURCES sont branchees (vague finale du 05/10/2026) : Lia par son
 * port, Notifications par la boite interne, Canaux, Messages directs et Ici
 * (le chatter de l'objet courant) par le port des conversations du socle
 * (`sourcesConversations.ts`, contrat `docs/socle/conversations.md`). Le
 * centre vit a la racine du shell : changer d'app ne le remonte pas.
 */
export interface CentreCommunicationProps {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  config: TakiConfig;
  /** Ce qui est ouvert a l'ecran : app, porte, fiche (libelles compris). */
  contexte: ContexteOuvert;
  /** Le contexte de Lia tel que le shell le calcule, SANS la fiche (le sujet y met la sienne). */
  contexteLia: AskContext;
  appEntries: readonly ShellAppEntry[];
  onOpenRecord: (record: { id: string; title: string; appId: string; doorId: string }, options?: { garderChatOuvert?: boolean }) => void;
  destinationFiche: (family: string, type?: string) => DestinationDeFiche | null;
  onOpenPlace: (place: { appId: string; doorId?: string }) => void;
  /** Ouvre une adresse de l'app (le lien d'une notification). */
  onNavigate: (chemin: string) => void;
}

type Vue = { source: SourceId; conversationId: string | null };
type Sujet = SujetEnCours & { archiveLe: number | null; modifieLe: number; persiste: boolean; partageA: string[]; partagePar: string | null; partageParPhoto?: string | null };
type Choix = { genre: 'direct' } | { genre: 'partage'; sujetId: string } | null;

function lireStorage(): Storage | null {
  try { return window.localStorage; } catch { return null; }
}

export function CentreCommunication(props: CentreCommunicationProps): JSX.Element | null {
  const { user } = useUser();
  if (!user) return null;
  return <Centre key={`${props.config.orgId}:${user.uid}`} {...props} uid={user.uid} nomDuCompte={user.displayName ?? user.email ?? 'Vous'} />;
}

function Centre({ open, onOpen, onClose, config, contexte, contexteLia, appEntries, onOpenRecord, destinationFiche, onOpenPlace, onNavigate, uid, nomDuCompte }: CentreCommunicationProps & { uid: string; nomDuCompte: string }): JSX.Element {
  const storage = useMemo(lireStorage, []);
  // Par personne et par poste (P9) ; l'ancienne cle commune sert de repli a la premiere lecture.
  const cleEtat = cleEtatCentre(config.orgId, uid);
  const [etat, setEtatBrut] = useState<EtatCentre>(() => lireEtatCentre(storage?.getItem(cleEtat) ?? storage?.getItem(CLE_ETAT_CENTRE) ?? null));
  const setEtat = useCallback((mise: (etat: EtatCentre) => EtatCentre): void => {
    setEtatBrut((courant) => { const suivant = mise(courant); ecrireEtatCentre(suivant, storage, cleEtat); return suivant; });
  }, [storage, cleEtat]);
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 639px)').matches);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 639px)');
    const update = (): void => setMobile(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  const settings = useIaSettingsPort();
  const launcher = settings?.launcher ?? 'mascot';
  const personnes = usePersonnesPort();
  const monPersonId = personnes?.moi?.personId ?? null;
  const moi = useMemo(() => {
    const nom = personnes?.moi?.displayName?.trim() || nomDuCompte;
    return { nom, initiales: initialesDe(nom), photoURL: personnes?.moi?.photoURL ?? null };
  }, [personnes?.moi?.displayName, personnes?.moi?.photoURL, nomDuCompte]);
  const mentions = useSourceDeMentions();

  /* LES CONVERSATIONS DU SOCLE : canaux, messages directs, chatter de l'objet courant, non lus geles. */
  const portConv = useConversationsCentrePort();
  const lister = useMemo(() => personnes ? () => personnes.lister() : null, [personnes]);
  // Chaque ouverture de la fenetre relit l'etat des chatters epingles (une base peut avoir eteint son chatter entre-temps).
  const [ouvertures, setOuvertures] = useState(0);
  useEffect(() => { if (open) setOuvertures((n) => n + 1); }, [open]);
  const socle = useSourcesConversations(portConv?.sources ?? portConv, contexte, lister, ouvertures, portConv?.pastilles);
  const raisonSansPort = portConv === null ? 'L’hôte ne monte pas le port des conversations.' : null;

  /* LES SUJETS DE LIA : ceux du port (en base), recouverts par ce que la session
     a de plus frais (les etapes d'outils, un tour en cours), les brouillons
     ouverts par « + » et pas encore ecrits, et les sujets que d'autres
     partagent avec moi (lecture seule, retour 18). */
  const port = useLiaConversationsPort();
  const [sujetsPort, setSujetsPort] = useState<LiaSujetView[]>([]);
  const [sujetsPrets, setSujetsPrets] = useState(port === null);
  useEffect(() => {
    if (port === null) return;
    return port.observer((liste) => { setSujetsPort(liste); setSujetsPrets(true); }, () => setSujetsPrets(true));
  }, [port]);
  const [partages, setPartages] = useState<LiaSujetPartageView[]>([]);
  const [raisonPartages, setRaisonPartages] = useState<string | null>(null);
  useEffect(() => {
    if (!port?.observerPartagesAvecMoi) return;
    return port.observerPartagesAvecMoi((liste) => { setPartages(liste); setRaisonPartages(null); },
      (cause) => setRaisonPartages(cause instanceof Error ? cause.message : 'Les sujets partagés ne se lisent pas.'));
  }, [port]);
  const [locaux, setLocaux] = useState<Map<string, SujetEnCours>>(() => new Map());
  const [brouillons, setBrouillons] = useState<SujetEnCours[]>([]);

  const sujets = useMemo<Sujet[]>(() => {
    const stockes = trierSujets(sujetsPort).map((sujet): Sujet => {
      const local = locaux.get(sujet.id);
      return { id: sujet.id, titre: local?.titre ?? sujet.titre, conversationId: sujet.conversationId,
        tours: local?.tours ?? sujet.tours.map(tourDepuisStockage), fiche: local?.fiche ?? sujet.fiche,
        archiveLe: sujet.archiveLe, modifieLe: sujet.modifieLe, persiste: true, partageA: sujet.partageA ?? [], partagePar: null };
    });
    const ids = new Set(stockes.map((sujet) => sujet.id));
    const recus = trierSujets(partages).filter((sujet) => !ids.has(sujet.id) && sujet.uid !== uid).map((sujet): Sujet => ({
      id: sujet.id, titre: sujet.titre, conversationId: sujet.conversationId, tours: sujet.tours.map(tourDepuisStockage), fiche: sujet.fiche,
      archiveLe: sujet.archiveLe, modifieLe: sujet.modifieLe, persiste: true, partageA: [], partagePar: socle.nomsParUid.get(sujet.uid) ?? 'une autre personne', partageParPhoto: socle.photosParUid.get(sujet.uid) ?? null }));
    return [...brouillons.filter((sujet) => !ids.has(sujet.id)).map((sujet): Sujet => ({ ...sujet, archiveLe: null, modifieLe: Date.now(), persiste: false, partageA: [], partagePar: null })), ...stockes, ...recus];
  }, [sujetsPort, partages, locaux, brouillons, uid, socle.nomsParUid, socle.photosParUid]);
  // La derniere valeur des sujets, lue par les ecritures ASYNCHRONES (la
  // reponse de Lia arrive apres le tour de l'utilisateur : elle doit s'ajouter
  // a la liste qui le contient, pas a celle d'avant).
  const sujetsRef = useRef(sujets);
  sujetsRef.current = sujets;

  const nouveauSujet = useCallback((): string => {
    const id = crypto.randomUUID();
    const sujet: SujetEnCours = { id, titre: TITRE_SUJET_DEFAUT, conversationId: crypto.randomUUID(), tours: [], fiche: null };
    setBrouillons((liste) => [sujet, ...liste.filter((item) => item.tours.length > 0)]);
    setEtat((courant) => ({ ...courant, source: 'lia', conversationParSource: { ...courant.conversationParSource, lia: id } }));
    return id;
  }, [setEtat]);

  // Le sujet regarde : celui que le poste a choisi s'il existe encore (un sujet archive s'ouvre
  // depuis « Archivees », en lecture seule), sinon le plus recent des vivants, sinon un sujet neuf.
  const memorise = etat.conversationParSource.lia;
  // Un sujet archive (depuis Canaux ou ailleurs) n'est jamais le sujet courant : la fenetre ouvre alors un sujet neuf.
  const sujetActifId = etat.source !== 'lia' ? null : sujetCourant(sujets, memorise);
  useEffect(() => {
    if (!open || !sujetsPrets || etat.source !== 'lia') return;
    if (sujetActifId === null || !sujets.some((sujet) => sujet.id === sujetActifId)) nouveauSujet();
  }, [open, sujetsPrets, etat.source, sujetActifId, sujets, nouveauSujet]);
  const sujetActif = sujets.find((sujet) => sujet.id === sujetActifId) ?? null;

  const onSujetChange = useCallback((id: string) => (mise: (sujet: SujetEnCours) => SujetEnCours, raison: 'tour' | 'fiche' | 'envoi'): Promise<void> => {
    const courant = sujetsRef.current.find((sujet) => sujet.id === id);
    if (!courant || courant.partagePar !== null) return Promise.resolve();
    let suivant = mise({ id: courant.id, titre: courant.titre, conversationId: courant.conversationId, tours: courant.tours, fiche: courant.fiche });
    if (suivant.titre === TITRE_SUJET_DEFAUT) {
      const premiere = suivant.tours.find((tour) => tour.role === 'user');
      if (premiere) suivant = { ...suivant, titre: titreDepuisQuestion(premiere.text) };
    }
    const fige = suivant;
    // La liste de reference suit tout de suite : deux ecritures rapprochees se voient l'une l'autre.
    sujetsRef.current = sujetsRef.current.map((sujet) => sujet.id === id ? { ...sujet, ...fige } : sujet);
    setLocaux((table) => new Map(table).set(id, fige));
    setBrouillons((liste) => liste.map((sujet) => sujet.id === id ? fige : sujet));
    if (raison === 'fiche' || fige.tours.length > 0) {
      const sauvegarde = port?.enregistrer({ id: fige.id, titre: fige.titre, conversationId: fige.conversationId, tours: fige.tours.map(tourPourStockage), fiche: fige.fiche }) ?? Promise.resolve();
      if (raison === 'envoi') return sauvegarde;
      return sauvegarde.catch(() => { /* L'ecriture a echoue : la conversation continue en memoire, le prochain tour reessaie. */ });
    }
    return Promise.resolve();
  }, [port]);

  const [liaEtat, setLiaEtat] = useState<{ pending: boolean; unread: LiaUnread }>({ pending: false, unread: null });
  const onEtatLia = useCallback((prochain: { pending: boolean; unread: LiaUnread }) => setLiaEtat(prochain), []);
  const raisonVerrou = liaEtat.pending ? 'Attendez la fin de cette réponse.' : null;

  const ficheOuverte = ficheDuContexte(contexte);
  const titreIci = socle.ancreIci === null ? null : socle.titreFixe ?? titreDeLAncre(socle.ancreIci, contexte);
  const iciEstUneFiche = socle.ancreIci !== null && 'family' in socle.ancreIci;
  /*
   * LE SUIVI DE LA FICHE EN MOTS (lot Q) : le catalogue de sa declaration,
   * retrouvee comme l'hote la retrouve (le type de la fiche, la porte qui la
   * montre par `destinationFiche`, l'instance de l'org sinon le modele). Un
   * ANCIEN changement a clefs se lit alors en mots, comme dans le volet.
   */
  const ficheIci = socle.ancreIci !== null && 'family' in socle.ancreIci ? socle.ancreIci : null;
  const cleFicheIci = ficheIci === null ? '' : `${ficheIci.family}/${ficheIci.recordId}`;
  const lireFicheIci = (portConv?.sources ?? portConv)?.lireFiche;
  const [typeIci, setTypeIci] = useState<{ cle: string; type: string | null } | null>(null);
  useEffect(() => {
    if (ficheIci === null || lireFicheIci === undefined) return;
    let vivant = true;
    lireFicheIci(ficheIci.family, ficheIci.recordId).then(
      (lue) => { if (vivant) setTypeIci({ cle: cleFicheIci, type: lue?.type ?? null }); },
      () => { if (vivant) setTypeIci({ cle: cleFicheIci, type: null }); },
    );
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleFicheIci, lireFicheIci]);
  const typeLuIci = typeIci !== null && typeIci.cle === cleFicheIci ? typeIci.type : undefined;
  const appDuSuiviIci = ficheIci === null || typeLuIci === undefined ? null : destinationFiche(ficheIci.family, typeLuIci ?? undefined)?.app ?? null;
  const instancesDeLOrg = useDeclarationsPort()?.instances;
  const familleIci = ficheIci?.family ?? null;
  const enMotsIci = useMemo(() => {
    if (familleIci === null || appDuSuiviIci === null || typeLuIci === undefined) return undefined;
    const catalogue = catalogueDeLaFiche(declarationDeLApp(appDuSuiviIci, appEntries, instancesDeLOrg), familleIci, typeLuIci);
    return catalogue === null ? undefined : (message: MessageVue) => messageDuSuiviEnMots(message, catalogue, socle.noms);
  }, [familleIci, appDuSuiviIci, typeLuIci, appEntries, instancesDeLOrg, socle.noms]);
  const [ouvertureFiche, setOuvertureFiche] = useState(false);
  const ouvrirFicheDuChatter = async (): Promise<void> => {
    const ancre = socle.ancreIci;
    if (!ancre || !('family' in ancre) || ouvertureFiche) return;
    setOuvertureFiche(true);
    try {
      if (ficheOuverte && contexte.family === ancre.family && ficheOuverte.recordId === ancre.recordId) {
        onOpenRecord({ id: ancre.recordId, title: ficheOuverte.titre, appId: ficheOuverte.appId, doorId: ficheOuverte.doorId }, { garderChatOuvert: true });
      } else {
        const fiche = await (portConv?.sources ?? portConv)?.lireFiche?.(ancre.family, ancre.recordId);
        if (!fiche) throw new Error('Cette fiche n’est plus accessible.');
        const destination = destinationFiche(ancre.family, fiche.type);
        if (!destination) throw new Error('Aucune porte accessible ne permet d’ouvrir cette fiche.');
        onOpenRecord({ id: ancre.recordId, title: fiche.titre, appId: destination.app, doorId: destination.porte }, { garderChatOuvert: true });
      }
    } catch (cause) {
      setErreurGeste(cause instanceof Error ? cause.message : 'La fiche n’a pas pu être ouverte.');
    } finally { setOuvertureFiche(false); }
  };
  // R10 : le bandeau « Fiche ouverte » suit la fiche DE L'ECRAN, et seulement si son ecran en fournit le chatter.
  const [chattersFournis, setChattersFournis] = useState<ReadonlySet<string>>(chattersDeFicheAnnonces);
  useEffect(() => {
    const relire = (): void => setChattersFournis(chattersDeFicheAnnonces());
    relire();
    window.addEventListener(EVENEMENT_CHATTER_FICHE, relire);
    return () => window.removeEventListener(EVENEMENT_CHATTER_FICHE, relire);
  }, []);
  const ancreDeLaFiche = contexte.family && contexte.recordId ? { family: contexte.family, recordId: contexte.recordId } : null;
  const bandeauFiche = ancreDeLaFiche && ficheOuverte && chattersFournis.has(`fiche:${ancreDeLaFiche.family}/${ancreDeLaFiche.recordId}`) ? ficheOuverte.titre : null;

  /* LA BOITE INTERNE (chantier notifications) : le meme total que la cloche du
     dock, compte par le serveur (G15). Les sujets Lia qui ont une « Reponse
     prete » non lue portent le point lime ; sans boite branchee, le centre
     garde son point de session. */
  const notifications = useNotificationsPort();
  const nonLus = notifications ? nonLusDuCentre(notifications.nonLus) : null;

  /* R12 : LES EPINGLES DE LA PERSONNE, gardes par le serveur (`lia:<sujetId>`, `conversation:<id>`) ;
     elles vivent dans le groupe « Epinglees », sous Notifications. */
  const epingles = useMemo(() => new Set(socle.epinglees), [socle.epinglees]);
  const [erreurGeste, setErreurGeste] = useState<string | null>(null);
  const basculerEpingleDe = (conversation: ConversationResume): void => {
    if (portConv === null || !conversation.ref) return;
    setErreurGeste(null);
    const geste = epingles.has(conversation.ref) ? portConv.epingles.desepingler(conversation.ref) : portConv.epingles.epingler(conversation.ref);
    void geste.catch(() => setErreurGeste('L’épingle n’a pas pu être enregistrée.'));
  };

  const [reglagesConversation, setReglagesConversation] = useState<{ ref: string; titre: string } | null>(null);
  const refPreference = (c: ConversationResume): string => c.source === 'lia' ? `lia:${partages.find(p => p.id === c.id)?.uid ?? uid}:${c.id}` : `conversation:${c.id}`;
  const basculerSourdine = (c: ConversationResume): void => {
    if (!notifications) return;
    const ref = refPreference(c), actuel = notifications.preferencesConversations.find(p => p.ref === ref);
    void notifications.enregistrerPreferenceConversation({ ...actuel, ref, titre: c.titre, sourdine: !actuel?.sourdine }).catch(e => setErreurGeste(String(e)));
  };
  const ouvrirReglagesExternes = (c: ConversationResume): void => { setReglagesConversation({ ref: refPreference(c), titre: c.titre }); };
  const titreDeCanal = useCallback((c: ConversationVue) => c.name || 'Canal', []);
  const titreDeDirectIci = useCallback((c: ConversationVue) => titreDeDirect(c, monPersonId, socle.noms), [monPersonId, socle.noms]);
  const conversations = useMemo<ConversationResume[]>(() => [
    ...(nonLus ? [{ id: 'boite', source: 'notifications' as const, titre: 'Notifications', nonLus: nonLus.total }] : []),
    ...sujets.filter((sujet) => sujet.persiste || sujet.tours.length > 0 || sujet.id === sujetActifId)
      .map((sujet): ConversationResume => {
        const ref = refDEpingle('lia', sujet.id);
        return { ...resumeDeSujet(sujet, false), nonLus: portConv?.pastilles?.parConversation[`lia:${partages.find(p => p.id === sujet.id)?.uid ?? uid}:${sujet.id}`] ?? 0,
          ref, epinglee: epingles.has(ref), renommable: sujet.partagePar === null, supprimable: sujet.partagePar === null,
          ...(sujet.partagePar ? { partagePar: sujet.partagePar } : {}) };
      }),
    ...resumesDeConversations(socle.canaux, 'canaux', socle.nonLus, titreDeCanal, epingles, socle.canauxGerables),
    ...resumesDeConversations(socle.directs, 'directs', socle.nonLus, titreDeDirectIci, epingles, new Set(), (c) => personneDeDirect(c, monPersonId, socle.personnes, initialesDuFil)),
    // Un chatter de fiche n'est dans aucun groupe : il n'apparait que s'il est epingle.
    ...socle.chattersEpingles.map(({ conversation, titre }): ConversationResume => ({ id: conversation.id, source: 'ici', titre, nonLus: socle.nonLus[conversation.id] ?? 0,
      ref: refDEpingle('conversation', conversation.id), epinglee: true, glyphe: 'fiche' })),
  ], [nonLus?.total, portConv?.pastilles, partages, uid, sujets, sujetActifId, liaEtat.unread, open, socle.canaux, socle.directs, socle.nonLus, socle.chattersEpingles, socle.canauxGerables, socle.personnes, monPersonId, epingles, titreDeCanal, titreDeDirectIci]);

  // La conversation du socle regardee : celle que le poste a choisie dans le groupe, sinon la premiere.
  const listeDuGroupe = etat.source === 'canaux' ? socle.canaux : etat.source === 'directs' ? socle.directs : [];
  const memoriseeDuGroupe = etat.conversationParSource[etat.source];
  const conversationSocle: ConversationVue | null = etat.source === 'canaux' || etat.source === 'directs'
    ? listeDuGroupe.find((c) => c.id === memoriseeDuGroupe) ?? listeDuGroupe[0] ?? null
    : etat.source === 'ici' ? socle.chatterIci : null;
  const vue: Vue = { source: etat.source, conversationId: etat.source === 'lia' ? sujetActifId : conversationSocle?.id ?? null };
  // Le message a mettre en avant (une notification « citation » ouverte) : le temps d'une conversation.
  const [messageVise, setMessageVise] = useState<{ conversationId: string; messageId: string } | null>(null);

  const refRegardee = !open ? null : etat.source === 'lia' && sujetActif ? `lia:${partages.find(p => p.id === sujetActif.id)?.uid ?? uid}:${sujetActif.id}` : conversationSocle ? `conversation:${conversationSocle.id}` : null;
  useEffect(() => { portConv?.pastilles?.regarder(refRegardee); return () => portConv?.pastilles?.regarder(null); }, [refRegardee, portConv?.pastilles?.regarder]);
  useEffect(() => { if (socle.chatterIci) portConv?.pastilles?.suivre(`conversation:${socle.chatterIci.id}`); }, [socle.chatterIci?.id, portConv?.pastilles?.suivre]);
  const [reponsesPersistantes, setReponsesPersistantes] = useState<Array<{ id: string; texte: string; at: number; docId?: string }>>([]);
  useEffect(() => {
    setReponsesPersistantes([]);
    if (!sujetActif || !port?.observerReponses || !open) return;
    return port.observerReponses(partages.find(p => p.id === sujetActif.id)?.uid ?? uid, sujetActif.id, setReponsesPersistantes);
  }, [sujetActif?.id, port?.observerReponses, open, uid, partages]);
  useEffect(() => {
    if (!refRegardee?.startsWith('lia:') || !reponsesPersistantes.length || !sujetActif) return;
    // Les reponses persistantes sont ajoutees au fil rendu, puis acquittees apres son affichage.
    const trame = requestAnimationFrame(() => portConv?.pastilles?.marquerLiaAffiche(refRegardee, new Date(Math.max(...reponsesPersistantes.map(r => r.at))), reponsesPersistantes.at(-1)?.docId));
    return () => cancelAnimationFrame(trame);
  }, [refRegardee, reponsesPersistantes, sujetActif?.tours, portConv?.pastilles?.marquerLiaAffiche]);

  const choisirSource = (source: SourceId): void => { setEtat((courant) => ({ ...courant, source })); };
  const choisirConversation = (source: SourceId, id: string): void => {
    setEtat((courant) => ({ ...courant, source, conversationParSource: { ...courant.conversationParSource, [source]: id } }));
  };
  /** Une conversation du socle designee par son id (notification, lien) : son groupe depend de son type. */
  const ouvrirConversationDuSocle = useCallback(async (conversationId: string, messageId?: string): Promise<void> => {
    if (messageId) setMessageVise({ conversationId, messageId });
    const connue = socle.canaux.find((c) => c.id === conversationId) ?? socle.directs.find((c) => c.id === conversationId);
    const c = connue ?? (portConv ? await portConv.lire(conversationId).catch(() => null) : null);
    if (c === null || c === undefined) { choisirSource('canaux'); return; }
    if (c.type === 'chatter') { socle.fixerAncreIci(c.ancre); choisirSource('ici'); return; }
    choisirConversation(c.type === 'canal' ? 'canaux' : 'directs', c.id);
  }, [socle.canaux, socle.directs, portConv, socle.fixerAncreIci]);

  // Un clic sur une @mention ouvre le direct avec cette personne : celui qui existe, sinon un neuf (creerDirect reprend l'existant).
  const ouvrirDirectAvec = useCallback(async (personId: string): Promise<void> => {
    if (portConv === null || personId === monPersonId) { choisirSource('directs'); return; }
    const connu = socle.directs.find((c) => c.participants.length === 2 && c.participants.includes(personId) && (monPersonId === null || c.participants.includes(monPersonId)));
    if (connu) { choisirConversation('directs', connu.id); return; }
    setErreurGeste(null);
    try { choisirConversation('directs', (await portConv.creerDirect([personId])).id); }
    catch (cause) { choisirSource('directs'); setErreurGeste(cause instanceof Error ? cause.message : 'Le message direct n’a pas pu être ouvert.'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portConv, monPersonId, socle.directs]);

  // La cloche du dock, une fiche, une notification ouvrent le centre sur une source, un sujet ou un objet.
  useEffect(() => {
    const ecouter = (event: Event): void => {
      const demande = (event as CustomEvent<OuvrirCentre>).detail;
      const geste = demande.lanceur ? gesteDuLanceur(open, etat.source, demande.source, demande.reprendre === true) : 'ouvrir';
      if (geste === 'fermer') { onClose(); return; }
      onOpen();
      // P9 : la mascotte rouvre la fenetre telle qu'on l'a reduite, sur la derniere conversation.
      if (geste === 'reprendre') return;
      if (demande.source === 'ici') {
        // R9 : on ne chatte que sur une fiche ; une ancre de porte ou d'app (vieux lien) ouvre la fenetre sans y basculer.
        const ancre = demande.ancre ? ancreDeFiche(ancreStricte(demande.ancre)) : null;
        if (demande.ancre && ancre === null) return;
        socle.fixerAncreIci(ancre, demande.ancre?.titre);
        choisirSource('ici');
        return;
      }
      if (demande.source === 'lia' && demande.conversationId) {
        const cible = sujetsRef.current.find((sujet) => sujet.conversationId === demande.conversationId);
        if (cible) { choisirConversation('lia', cible.id); return; }
      }
      if (demande.conversationId && demande.source !== 'lia') { void ouvrirConversationDuSocle(demande.conversationId, demande.messageId); return; }
      if (demande.source === 'directs' && demande.personId) { void ouvrirDirectAvec(demande.personId); return; }
      choisirSource(demande.source);
    };
    window.addEventListener(EVENEMENT_OUVRIR_CENTRE, ecouter);
    return () => window.removeEventListener(EVENEMENT_OUVRIR_CENTRE, ecouter);
  });

  /* Les gestes de creation : un sujet Lia en memoire, un canal (fenetre), un direct (choix de personnes). */
  const [fenetreCanal, setFenetreCanal] = useState(false);
  const [choix, setChoix] = useState<Choix>(null);
  const [erreurChoix, setErreurChoix] = useState<string | null>(null);
  const ajouter = (source: SourceId): void => {
    if (source === 'lia') nouveauSujet();
    else if (source === 'canaux' && portConv) setFenetreCanal(true);
    else if (source === 'directs' && portConv) { setErreurChoix(null); setChoix({ genre: 'direct' }); }
  };
  const creerCanal = async (canal: { nom: string; prive: boolean; participants: string[] }): Promise<void> => {
    if (!portConv) return;
    const c = await portConv.creerCanal({ nom: canal.nom, prive: canal.prive, ...(canal.prive ? { participants: canal.participants } : {}) });
    setFenetreCanal(false);
    choisirConversation('canaux', c.id);
  };
  const validerChoix = async (personIds: string[]): Promise<void> => {
    if (choix === null) return;
    try {
      if (choix.genre === 'direct' && portConv) {
        const c = await portConv.creerDirect(personIds);
        choisirConversation('directs', c.id);
      } else if (choix.genre === 'partage' && port?.partager) {
        const sujet = sujets.find((s) => s.id === choix.sujetId);
        const avant = sujet?.partageA ?? [];
        const retires = avant.filter((p) => !personIds.includes(p));
        const ajoutes = personIds.filter((p) => !avant.includes(p));
        if (ajoutes.length) await port.partager(choix.sujetId, ajoutes);
        if (retires.length && port.retirerPartage) await port.retirerPartage(choix.sujetId, retires);
      }
      setChoix(null);
    } catch (cause) {
      setErreurChoix(cause instanceof Error ? cause.message : 'Le geste a échoué.');
    }
  };

  /* Le rail : deplie en elargissant la FENETRE vers la gauche (e2), ou superpose
     au fil quand la fenetre du navigateur est etroite (e3, sous 1100 px). */
  const [navigateur, setNavigateur] = useState(() => ({ width: window.innerWidth, height: window.innerHeight }));
  useEffect(() => {
    const mesurer = (): void => setNavigateur({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', mesurer);
    return () => window.removeEventListener('resize', mesurer);
  }, []);
  const superpose = mobile || listeSeSuperpose(navigateur.width);
  const [superpositionOuverte, setSuperpositionOuverte] = useState(false);
  // Fenetre large, la liste est TOUJOURS la (retouches du 06/10/2026) : on la regle par sa bordure, on ne la replie plus.
  const railDeplie = superpose ? superpositionOuverte : true;
  const deplier = (deplie: boolean): void => {
    if (superpose) setSuperpositionOuverte(deplie);
  };
  // La largeur de la liste glissee : suivie en direct pendant le geste, ecrite au poste a la fin.
  const [largeurGlissee, setLargeurGlissee] = useState<number | null>(null);
  const glissementListe = useRef<{ x: number; largeur: number } | null>(null);
  // Un chatter de fiche epingle (groupe « Epinglees ») s'ouvre sur sa fiche, dans Ici.
  const choisirConversationDansListe = (source: SourceId, id: string): void => {
    if (source === 'ici') void ouvrirConversationDuSocle(id); else choisirConversation(source, id);
    if (superpose) setSuperpositionOuverte(false);
  };
  const choisirSourceDansListe = (source: SourceId): void => { choisirSource(source); if (superpose) setSuperpositionOuverte(false); };

  // La TAILLE de la fenetre est celle du panneau de main (v26.63) : poignees en haut et a gauche,
  // bouton Agrandir / Taille normale, retenue par poste (`liaPanelSize.ts`). La liste depliee
  // elargit la fenetre vers la gauche (e2), en plus de cette taille.
  const panelSize = useLiaPanelSize();

  // La fenetre flotte AU-DESSUS de l'app : elle ne lui prend aucune place (retour 19).
  const fenetre = geometrieFenetre(panelSize.size, railDeplie, navigateur, superpose);
  const largeurListe = bornerLargeurListe(largeurGlissee ?? etat.largeurListe, fenetre.width);
  const retenirLargeurListe = (largeur: number): void => {
    setLargeurGlissee(null);
    setEtat((courant) => ({ ...courant, largeurListe: bornerLargeurListe(largeur, fenetre.width) }));
  };


  // Clavier : Echap reduit puis ferme ; Ctrl Maj L ouvre, et sous 1100 px ouvre et ferme la liste superposee.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (estRaccourciRail(event) && (!open || superpose)) {
        event.preventDefault();
        if (!open) { onOpen(); return; }
        deplier(!railDeplie);
        return;
      }
      if (!open || event.key !== 'Escape') return;
      if (suppression !== null || choix !== null || fenetreCanal) return;
      // Un menu, une liste, une confirmation ou un renommage ouverts : Echap les ferme, pas la fenetre (ecart I).
      if (!echapFermeLaFenetre(event, document)) return;
      event.preventDefault();
      if (superpose && superpositionOuverte) { setSuperpositionOuverte(false); return; }
      onClose();
    };
    // En CAPTURE sur `window` : la fenetre traite Echap avant l'app, et le marque traite (preventDefault) ;
    // le raccourci de la fiche (`useScreen`) ignore alors la touche et la fiche ne recule pas (ecart I, passe 2).
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  /* R4 : RENOMMER EN PLACE (double-clic ou menu de la ligne, enregistrement a la validation) et SUPPRIMER,
     c'est-a-dire archiver (menu, avec confirmation). Un sujet de Lia a moi, un canal que je gere ; un direct se
     supprime POUR MOI. Les archives se consultent dans l'app Canaux, jamais dans la fenetre. */
  const renommerConversation = async (conversation: ConversationResume, titre: string): Promise<void> => {
    const propre = titre.trim();
    if (propre === '' || propre === conversation.titre || !conversation.renommable) return;
    setErreurGeste(null);
    if (conversation.source === 'lia') {
      const cible = sujets.find((sujet) => sujet.id === conversation.id);
      if (!cible || cible.partagePar !== null) return;
      setLocaux((table) => new Map(table).set(cible.id, { id: cible.id, titre: propre, conversationId: cible.conversationId, tours: cible.tours, fiche: cible.fiche }));
      setBrouillons((liste) => liste.map((sujet) => sujet.id === cible.id ? { ...sujet, titre: propre } : sujet));
      if (cible.persiste) await port?.renommer(cible.id, propre).catch(() => setErreurGeste('Le nouveau titre n’a pas pu être enregistré.'));
      return;
    }
    if (portConv === null) return;
    await portConv.renommer(conversation.id, propre).catch((cause: unknown) => setErreurGeste(cause instanceof Error ? cause.message : 'Le canal n’a pas pu être renommé.'));
  };
  const [suppression, setSuppression] = useState<ConversationResume | null>(null);
  const supprimerConversation = async (conversation: ConversationResume): Promise<void> => {
    setErreurGeste(null);
    if (conversation.ref && epingles.has(conversation.ref)) void portConv?.epingles.desepingler(conversation.ref).catch(() => undefined);
    if (conversation.source === 'lia') {
      const cible = sujets.find((sujet) => sujet.id === conversation.id);
      if (!cible || cible.partagePar !== null) return;
      setEtat((courant) => ({ ...courant, conversationParSource: { ...courant.conversationParSource, lia: undefined } }));
      if (!cible.persiste) { setBrouillons((liste) => liste.filter((sujet) => sujet.id !== cible.id)); return; }
      setLocaux((table) => { const suivante = new Map(table); suivante.delete(cible.id); return suivante; });
      await port?.archiver(cible.id).catch(() => setErreurGeste('Le sujet n’a pas pu être supprimé.'));
      return;
    }
    if (portConv === null || (conversation.source !== 'canaux' && conversation.source !== 'directs')) return;
    const source = conversation.source;
    setEtat((courant) => courant.conversationParSource[source] === conversation.id
      ? { ...courant, conversationParSource: { ...courant.conversationParSource, [source]: undefined } } : courant);
    await portConv.archiver(conversation.id).catch((cause: unknown) => setErreurGeste(cause instanceof Error ? cause.message : 'La conversation n’a pas pu être supprimée.'));
  };


  // Une notification s'ouvre sur son objet : un sujet de Lia ou une conversation dans le centre, le reste par l'adresse.
  const ouvrirNotification = (chemin: string, notification: { objet: { type: string; id: string } | null }): void => {
    if (notification.objet?.type === 'lia') {
      const cible = sujets.find((sujet) => sujet.conversationId === notification.objet!.id);
      if (cible) { choisirConversation('lia', cible.id); return; }
    }
    if (notification.objet?.type === 'conversation') { void ouvrirConversationDuSocle(notification.objet.id, messageDuChemin(chemin)); return; }
    if (chemin) onNavigate(chemin);
  };
  const ouvrirFiche = (fiche: FicheSuivie): void => onOpenRecord({ id: fiche.recordId, title: fiche.titre, appId: fiche.appId, doorId: fiche.doorId });
  const sourceActive = sourceDuCentre(vue.source);
  const lectureSeule = sujetActif?.partagePar ? `Partagé par ${sujetActif.partagePar} : lecture seule.`
    : sujetActif?.archiveLe != null ? 'Sujet archivé : désarchivez-le pour écrire.' : null;
  const sansPort = (source: typeof sourceActive): JSX.Element => <EmplacementAVenir source={{ ...source, raison: raisonSansPort ?? source.raison }} ficheOuverte={ficheOuverte} onOuvrirFiche={ouvrirFiche} compact />;

  const zone5 = (compact: boolean): JSX.Element => {
    if (vue.source === 'notifications') return notifications ? <NotificationsBoite className="min-w-0 flex-1" onOuvrir={ouvrirNotification} />
      : <EmplacementAVenir source={{ ...sourceActive, raison: 'L’hôte ne monte pas la boîte de notifications.' }} ficheOuverte={ficheOuverte} compact={compact} />;
    if (vue.source === 'canaux' || vue.source === 'directs') {
      if (portConv === null) return sansPort(sourceActive);
      if (!socle.pret) return <Attente texte={vue.source === 'canaux' ? 'Lecture des canaux…' : 'Lecture des messages directs…'} />;
      if (conversationSocle === null) return <Vide titre={vue.source === 'canaux' ? 'Aucun canal' : 'Aucun message direct'}
        texte={vue.source === 'canaux' ? 'Le « + » du groupe Canaux crée le premier, public ou privé.' : 'Le « + » du groupe Messages directs choisit avec qui écrire.'} compact={compact} />;
      return <ConversationSocle key={conversationSocle.id} conversationId={conversationSocle.id} port={portConv.fil} moi={monPersonId} entrees={mentions.entrees} typeDeConversation={conversationSocle.type} compact={compact}
        messageId={messageVise?.conversationId === conversationSocle.id ? messageVise.messageId : null}
        vide={vue.source === 'canaux' ? `Aucun message dans ${conversationSocle.prive ? '' : '#'}${conversationSocle.name}. Le premier est à vous.` : 'Aucun message encore. Le premier est à vous.'}
        placeholder={vue.source === 'canaux' ? `Écrire dans ${conversationSocle.prive ? '' : '#'}${conversationSocle.name}…` : `Écrire à ${titreDeDirectIci(conversationSocle)}…`}
        onActivite={() => socle.marquerLu(conversationSocle.id)} ficheVue={ficheOuverte?.titre ?? null} />;
    }
    if (vue.source === 'ici') {
      if (portConv === null) return sansPort(sourceActive);
      if (socle.ancreIci === null) return <Vide titre="Rien d’ouvert" texte="Ouvrez une fiche pour rejoindre son suivi." compact={compact} />;
      if (socle.chatterIciEnChargement) return <Attente texte="Chargement de la conversation…" />;
      if (socle.chatterIciErreur) return <Vide titre="Conversation indisponible" texte="La conversation n’a pas pu être chargée. Rouvrez-la pour réessayer." compact={compact} />;
      const id = socle.chatterIci?.id ?? null;
      // La cle suit l'ANCRE, pas l'id du chatter : le premier envoi cree le chatter (`avantEnvoi`), et le
      // composeur doit rester monte pendant ce temps, sinon les messages tapes ensuite partiraient avec lui.
      return <ConversationSocle key={JSON.stringify(socle.ancreIci)} conversationId={id} port={portConv.fil} moi={monPersonId} entrees={mentions.entrees} compact={compact}
        messageId={id && messageVise?.conversationId === id ? messageVise.messageId : null}
        vide={socle.chatterIciEteint ? `Le suivi est éteint pour les fiches de cette base : rien ne s’écrit sur ${titreIci ?? 'cette fiche'}.` : socle.ficheIciArchivee ? `Aucun message sur ${titreIci ?? 'cette fiche'}.` : `Aucun message sur ${titreIci ?? 'cet objet'}. Le premier crée le suivi.`} placeholder={`Écrire sur ${titreIci ?? 'cet objet'}…`}
        avantEnvoi={id === null && !socle.chatterIciEteint && !socle.ficheIciArchivee ? socle.ouvrirChatterIci : undefined} onActivite={() => { if (id) socle.marquerLu(id); }}
        lectureSeule={raisonLectureSeuleIci(socle)}
        cleDeBrouillon={id === null ? `ancre:${JSON.stringify(socle.ancreIci)}` : undefined}
        onEnvoye={() => { if (socle.ancreIci) signalerChatterEcrit(socle.ancreIci); }} enMots={enMotsIci} />;
    }
    if (sujetActif === null) return <Attente texte={sujetsPrets ? 'Ouverture du sujet…' : 'Lecture de vos sujets…'} />;
    return <ConversationLia key={sujetActif.id} sujet={{ ...sujetActif, tours: [...sujetActif.tours, ...reponsesPersistantes.filter(r => !sujetActif.tours.some(t => t.role === 'assistant' && t.text === r.texte)).map(r => ({ id: r.id, role: 'assistant' as const, text: r.texte, at: r.at }))] }} onSujetChange={onSujetChange(sujetActif.id)} config={config} contexte={contexteLia}
      ficheOuverte={ficheOuverte} appEntries={appEntries} onOpenRecord={onOpenRecord} onOpenPlace={onOpenPlace} open={open} compact={compact}
      // Un sujet PARTAGE se lit chez le lecteur : les questions sont celles du proprietaire, signees de son nom.
      moi={sujetActif.partagePar ? { nom: sujetActif.partagePar, initiales: initialesDe(sujetActif.partagePar), photoURL: sujetActif.partageParPhoto ?? null } : moi}
      onEtat={onEtatLia} storageKey={`taki:chat:${config.orgId}:${uid}:${sujetActif.id}`} uid={uid} lectureSeule={lectureSeule} notifier={false} />;
  };

  const style: CSSProperties | undefined = mobile ? undefined
    : { right: FENETRE_MARGE, bottom: FENETRE_MARGE, width: fenetre.width, height: fenetre.height };

  // La mascotte, en bas a droite comme sur main (v26.63). Fenetre fermee, elle la ROUVRE sur la derniere
  // conversation (P9) ; ouverte, elle bascule sur Lia, et ne ferme que si Lia est deja a l'ecran.
  const lancer = (): void => ouvrirCentre({ source: 'lia', lanceur: true, reprendre: true });
  const lanceur = <div className="pointer-events-none fixed inset-0 z-50">
    <LiaLauncher open={open} onOpen={lancer} onClose={lancer} pending={liaEtat.pending} unread={liaEtat.unread} launcher={launcher} totalPastilles={portConv?.pastilles?.total ?? 0} arrivees={portConv?.pastilles?.arrivees ?? 0} mascotteAnimee={(notifications?.ready ?? false) && (notifications?.prefs.mascotteAnimee ?? true)} />
  </div>;

  if (!open) return lanceur;

  const fenetres = <>
    <DialogueNotificationsConversation conversation={reglagesConversation} onClose={() => setReglagesConversation(null)} />
    {(portConv?.pastilles?.erreur || notifications?.erreur) && <p role="alert" className="fixed bottom-4 left-4 z-50 rounded-md bg-surface p-3 text-sm text-danger">{portConv?.pastilles?.erreur ?? notifications?.erreur}</p>}
    <FenetreSupprimer conversation={suppression} onClose={() => setSuppression(null)} onValider={() => { const cible = suppression; setSuppression(null); if (cible) void supprimerConversation(cible); }} />
    <FenetreCanal open={fenetreCanal} personnes={socle.personnes} moi={monPersonId} onClose={() => setFenetreCanal(false)} onValider={creerCanal} />
    <ChoixDePersonnes open={choix !== null} titre={choix?.genre === 'partage' ? 'Partager le sujet' : 'Nouveau message direct'} verbe={choix?.genre === 'partage' ? 'Partager avec' : 'Écrire à'}
      personnes={socle.personnes} moi={monPersonId} initiales={choix?.genre === 'partage' ? sujets.find((s) => s.id === choix.sujetId)?.partageA ?? [] : []}
      onClose={() => setChoix(null)} onValider={validerChoix} erreur={erreurChoix} />
  </>;
  // L'icone de l'en-tete porte deja le diese (ou le cadenas) : le titre d'un canal est son nom seul (verification, ecart E).
  const titreDuSocle = conversationSocle === null ? null : vue.source === 'canaux' ? conversationSocle.name : vue.source === 'directs' ? titreDeDirectIci(conversationSocle) : titreIci;
  const nonLusDuSocle = conversationSocle ? socle.nonLus[conversationSocle.id] ?? 0 : 0;

  /* ---- La fenetre flottante (hub-d, hub-e1, hub-e2, hub-e3), au-dessus de l'app ; agrandir et retrecir comme sur main (v26.63). ---- */
  const titre = vue.source === 'lia' ? sujetActif?.titre ?? 'Lia' : vue.source === 'ici' ? titreIci ?? 'Ici' : titreDuSocle ?? sourceActive.libelle;
  const sousTitre = vue.source === 'lia' ? (sujetActif?.partagePar ? `Lia · partagé par ${sujetActif.partagePar}` : libelleDuContexte(contexte) ? `Lia · ${libelleDuContexte(contexte)}` : 'Lia de Taki')
      : vue.source === 'notifications' && nonLus ? (nonLus.total === 0 ? 'Tout est lu' : `${nonLus.total} non ${nonLus.total > 1 ? 'lues' : 'lue'}`)
        : vue.source === 'ici' ? (iciEstUneFiche ? 'Suivi de la fiche' : 'Suivi') : conversationSocle ? `${sourceActive.libelle}${nonLusDuSocle > 0 ? ` · ${nonLusDuSocle} non lu${nonLusDuSocle > 1 ? 's' : ''}` : ''}`
          : raisonSansPort ? `${sourceActive.libelle} · non branché` : sourceActive.libelle;
  const bouton = 'flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-accent-label hover:bg-accent-press focus-visible:shadow-ring';
  const libelleAjout = vue.source === 'canaux' ? 'Nouveau canal' : vue.source === 'directs' ? 'Nouveau message direct' : 'Nouveau sujet avec Lia';
  return <>
    {lanceur}
    <aside role={mobile ? 'dialog' : 'region'} aria-modal={mobile || undefined} aria-label="Centre de communication" id="taki-chat-panel" style={style} data-taki-hors-ecran=""
      data-lia-size={mobile ? undefined : panelSize.large ? 'large' : panelSize.custom ? 'custom' : 'default'} data-rail={railDeplie ? 'deplie' : 'replie'}
      className={cn('z-20 flex flex-col overflow-hidden bg-surface', mobile ? 'fixed inset-0'
        : 'fixed rounded-lg border border-border shadow-fenetre duration-200 motion-reduce:transition-none', !mobile && (panelSize.resizing ? 'transition-none select-none' : 'transition-[width,height]'))}>
      {!mobile && <>
        <div aria-hidden="true" {...panelSize.handle('y')} className="absolute inset-x-6 top-0 z-10 h-1.5 cursor-ns-resize touch-none" />
        <div aria-hidden="true" {...panelSize.handle('x')} className="absolute inset-y-6 left-0 z-10 w-1.5 cursor-ew-resize touch-none" />
        <div role="separator" aria-orientation="vertical" aria-label="Redimensionner Lia" title="Redimensionner (double-clic : taille normale)" tabIndex={0}
          aria-valuemin={360} aria-valuemax={panelSize.max.width} aria-valuenow={panelSize.size.width}
          {...panelSize.handle('both')} onKeyDown={panelSize.onHandleKey}
          className="group/resize absolute left-0 top-0 z-20 flex h-7 w-7 cursor-nwse-resize touch-none items-start justify-start rounded-tl-lg p-[7px] outline-none">
          <span aria-hidden="true" className="h-2.5 w-2.5 rounded-tl-[6px] border-l-2 border-t-2 border-accent-label opacity-0 transition-opacity group-hover/resize:opacity-100 group-focus-visible/resize:opacity-100 motion-reduce:transition-none" />
        </div>
      </>}
      <header className="flex h-10 shrink-0 items-center gap-1 border-b border-accent-border bg-accent-hover pl-3 pr-2 text-accent-label">
        <span aria-hidden="true" className="mr-1 flex h-5 w-5 shrink-0 items-center justify-center">{vue.source === 'lia' ? <LiaHead size={18} /> : vue.source === 'canaux' && conversationSocle?.prive ? <IconPadlockSquare1 size={15} aria-label="Canal privé" /> : <IconeDeSource id={vue.source} size={15} />}</span>
        {/* R5 : aucun chevron ni liste deroulante dans l'en-tete ; on change de conversation par la liste de gauche. */}
        <div className="flex min-w-0 flex-1 items-baseline gap-2">
          <span className="min-w-0 truncate text-[0.9375rem] font-semibold leading-5">{titre}</span>
          <span className="min-w-0 truncate text-xs leading-4">{sousTitre}</span>
        </div>
        {vue.source === 'ici' && iciEstUneFiche && <button type="button" onClick={() => void ouvrirFicheDuChatter()} disabled={ouvertureFiche}
          className="mr-2 flex h-7 shrink-0 items-center gap-1.5 rounded-sm px-2 text-xs font-semibold hover:bg-accent-press focus-visible:shadow-ring disabled:opacity-50"
          aria-label={`Ouvrir la fiche ${titreIci ?? ''}`}>
          {ouvertureFiche ? 'Ouverture…' : 'Ouvrir la fiche'} <span aria-hidden="true">↗</span>
        </button>}
        {vue.source === 'ici' && iciEstUneFiche && portConv && (() => {
          const chatter = socle.chatterIci ?? socle.chattersEpingles.find(({ conversation }) => memeAncre(conversation.ancre ?? null, socle.ancreIci))?.conversation;
          const ref = chatter ? refDEpingle('conversation', chatter.id) : null;
          const epinglee = ref !== null && epingles.has(ref);
          // Un chatter encore sans message s'epingle aussi : il est cree d'abord, vide.
          const epingler = async (): Promise<void> => {
            const id = chatter?.id ?? await socle.ouvrirChatterIci().catch(() => null);
            if (id === null) { setErreurGeste('Le suivi n’a pas pu être épinglé.'); return; }
            const cible = refDEpingle('conversation', id);
            basculerEpingleDe({ id, source: 'ici', titre: titreIci ?? 'Fiche', nonLus: 0, ref: cible, epinglee: epingles.has(cible) });
          };
          return <button type="button" onClick={() => void epingler()} disabled={!chatter && (socle.chatterIciEnChargement || socle.chatterIciErreur)}
            aria-pressed={epinglee} aria-label={epinglee ? 'Désépingler ce suivi' : 'Épingler ce suivi'} title={epinglee ? 'Désépingler' : 'Épingler en haut de la liste'}
            className={bouton}><IconeEpingle pleine={epinglee} size={12} /></button>;
        })()}
        {!mobile && <button type="button" onClick={panelSize.toggleLarge} aria-label={panelSize.large ? 'Taille normale' : 'Agrandir Lia'} title={panelSize.large ? 'Taille normale' : 'Agrandir'} aria-pressed={panelSize.large}
          className={cn(bouton, panelSize.large && 'bg-accent-press')}>{panelSize.large ? <IconWindowShrink size={14} /> : <IconWindowExpand size={14} />}</button>}
        <button type="button" onClick={() => { if (raisonVerrou === null) ajouter(vue.source === 'canaux' || vue.source === 'directs' ? vue.source : 'lia'); }} aria-label={libelleAjout} aria-disabled={raisonVerrou !== null || undefined}
          title={raisonVerrou ?? libelleAjout} className={bouton}><IconWindowAdd size={14} /></button>
        <button type="button" onClick={onClose} aria-label="Réduire le centre" title="Réduire" className={bouton}><IconWindowMinimize size={14} /></button>
      </header>
      {bandeauFiche !== null && !(vue.source === 'ici' && memeAncre(socle.ancreIci, ancreDeLaFiche)) &&
        <div className="flex h-9 shrink-0 items-center gap-2 border-b border-border bg-surface-1 px-3 text-xs text-ink-600" data-bandeau="fiche-ouverte">
          <IconeDeSource id="ici" size={13} />
          <span className="min-w-0 truncate">Fiche ouverte : <b className="font-semibold text-ink-900">{bandeauFiche}</b></span>
          <button type="button" onClick={() => { socle.fixerAncreIci(ancreDeLaFiche, bandeauFiche); choisirSource('ici'); }} className="ml-auto flex shrink-0 items-center gap-1 font-semibold text-ink-700 hover:text-ink-900 focus-visible:shadow-ring" title="Ouvrir la conversation de cette fiche">
            Ouvrir conv. <IconArrowRight size={11} />
          </button>
        </div>}
      <div className="relative flex min-h-0 flex-1">
        <RailDuCentre source={vue.source} onSource={choisirSourceDansListe} conversations={conversations} conversationActive={vue.conversationId}
          onConversation={choisirConversationDansListe} onAjouter={ajouter} onSourdine={basculerSourdine} onReglagesExternes={ouvrirReglagesExternes} sourdines={new Set(conversations.filter(c => notifications?.preferencesConversations.some(p => p.ref === refPreference(c) && p.sourdine)).map(c => c.ref ?? ''))} onEpingler={basculerEpingleDe} onRenommer={renommerConversation} onSupprimer={setSuppression} ordreEpingles={socle.epinglees} deplie={railDeplie && !superpose} onDeplier={deplier} raisonSansPort={raisonSansPort}
          raisonVerrou={raisonVerrou} largeur={superpose ? undefined : largeurListe} />
        {/* La BORDURE de la liste se glisse (souris, doigt) ou se regle au clavier (fleches) ; la largeur est retenue par poste. */}
        {!superpose && <div role="separator" aria-orientation="vertical" aria-label="Largeur de la liste des conversations" title="Glisser pour élargir ou réduire la liste"
          tabIndex={0} aria-valuemin={180} aria-valuemax={Math.max(180, fenetre.width - 280)} aria-valuenow={largeurListe} data-bordure-liste
          className="absolute inset-y-0 z-10 w-2 -translate-x-1/2 cursor-col-resize touch-none outline-none hover:bg-accent-tint-border focus-visible:bg-accent-tint-border"
          style={{ left: largeurListe }}
          onPointerDown={(event) => { glissementListe.current = { x: event.clientX, largeur: largeurListe }; event.currentTarget.setPointerCapture(event.pointerId); }}
          onPointerMove={(event) => { const depart = glissementListe.current; if (depart) setLargeurGlissee(bornerLargeurListe(depart.largeur + event.clientX - depart.x, fenetre.width)); }}
          onPointerUp={(event) => { const depart = glissementListe.current; glissementListe.current = null; if (depart) retenirLargeurListe(depart.largeur + event.clientX - depart.x); }}
          onPointerCancel={() => { glissementListe.current = null; setLargeurGlissee(null); }}
          onKeyDown={(event) => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
            event.preventDefault();
            retenirLargeurListe(largeurListe + (event.key === 'ArrowRight' ? 16 : -16));
          }} />}
        {/* P21 : la conversation se detache de la liste par une OMBRE DOUCE (le token du layout general), pas par un trait. */}
        <div data-zone-conversation className="relative z-[1] flex min-h-0 min-w-0 flex-1 bg-surface shadow-contenu">{zone5(true)}</div>
        {superpose && superpositionOuverte && <>
          <button type="button" aria-label="Fermer la liste" onClick={() => setSuperpositionOuverte(false)} className="absolute inset-0 z-10 cursor-default bg-scrim backdrop-blur-[1px]" />
          <div className="absolute inset-y-0 left-11 z-20 flex shadow-pop">
            <ListeDuRail source={vue.source} onSource={choisirSourceDansListe} conversations={conversations} conversationActive={vue.conversationId}
              onConversation={choisirConversationDansListe} onAjouter={(source) => { ajouter(source); setSuperpositionOuverte(false); }} onSourdine={basculerSourdine} onReglagesExternes={ouvrirReglagesExternes} sourdines={new Set(conversations.filter(c => notifications?.preferencesConversations.some(p => p.ref === refPreference(c) && p.sourdine)).map(c => c.ref ?? ''))} onEpingler={basculerEpingleDe} onRenommer={renommerConversation} onSupprimer={setSuppression} ordreEpingles={socle.epinglees} deplie onDeplier={deplier} raisonSansPort={raisonSansPort}
              raisonVerrou={raisonVerrou} />
          </div>
        </>}
      </div>
      {raisonPartages && vue.source === 'lia' && <p role="status" className="shrink-0 border-t border-border-soft px-3 py-1 text-[0.6875rem] text-ink-500">Sujets partagés : {raisonPartages}</p>}
      {erreurGeste && <p role="alert" className="shrink-0 border-t border-border-soft px-3 py-1 text-[0.6875rem] text-danger-ink">{erreurGeste}</p>}
    </aside>
    {fenetres}
  </>;
}

function Attente({ texte }: { texte: string }): JSX.Element {
  return <div className="flex flex-1 items-center justify-center text-sm text-ink-500">{texte}</div>;
}

/** Un etat vide qui dit quoi faire (G25 : le geste est un bouton ailleurs, ici la phrase qui le nomme). */
function Vide({ titre, texte, compact }: { titre: string; texte: string; compact: boolean }): JSX.Element {
  return <div className={cn('flex min-h-0 flex-1 flex-col items-center justify-center text-center', compact ? 'px-5 py-6' : 'px-10 py-12')}>
    <h2 className={cn('font-semibold text-ink-900', compact ? 'text-base leading-6' : 'text-lg leading-7')}>{titre}</h2>
    <p className={cn('mt-1.5 max-w-sm text-ink-500', compact ? 'text-[0.8125rem] leading-5' : 'text-sm leading-5')}>{texte}</p>
  </div>;
}

/** R4 : « Supprimer » une conversation, c'est l'archiver ; la fenetre dit ou elle se retrouve (G6, confirmation). */
function FenetreSupprimer({ conversation, onClose, onValider }: { conversation: ConversationResume | null; onClose: () => void; onValider: () => void }): JSX.Element {
  // « Supprimer » archive : la phrase dit ou la conversation va, avec le mot de l'app Canaux (son mode « Archives »).
  const texte = conversation === null ? '' : conversation.source === 'lia'
    ? `Le sujet « ${conversation.titre} » quitte la fenêtre et va dans les Archives de Canaux, d’où il se restaure.`
    : conversation.source === 'directs'
      ? `Ce message direct avec ${conversation.titre} quitte votre liste et va dans vos Archives de Canaux, d’où il se restaure. L’autre personne le garde.`
      : `Le canal « ${conversation.titre} » est archivé pour toute l’équipe : il va dans les Archives de Canaux, d’où il se restaure.`;
  return <Modal open={conversation !== null} onClose={onClose} title="Supprimer la conversation" size="sm"
    footer={<><Button variant="secondary" size="sm" onClick={onClose}>Annuler</Button><Button variant="danger" size="sm" onClick={onValider}>Supprimer</Button></>}>
    <p className="text-sm leading-5 text-ink-700">{texte}</p>
  </Modal>;
}
